import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Tag from 'shopper/components/Tag/V2';
import Text from 'shopper/components/Text';

import Image from 'components/Image';

import useCouponPicker from 'hooks/useCouponPicker';
import useExclusiveCoupon from 'hooks/useExclusiveCoupon';

import { sendEvent } from 'lib/gtag';
import { getStoreImageUrl } from 'lib/image';
import placeholder from 'lib/placeholder';

import COUPONS from 'constants/coupons';

const getSimplifiedCouponDiscount = (couponDiscountShort) =>
  couponDiscountShort.split(' OFF')[0];

const CouponListItem = ({
  className,
  couponCode,
  couponDiscountShort,
  couponId,
  couponTag = [],
  couponTitle,
  gaEventCategory,
  isCouponVisible = false,
  storeImage = null,
  storeName,
  storeSlug,
  visibility,
  withCouponStoreCover = false,
}) => {
  const {
    formatGaEventActionByVisibility,
    isCouponBlocked,
    onUnloggedUserTriesOpenExclusiveCoupon,
  } = useExclusiveCoupon();
  const { openCouponPicker, retrieveCoupon } = useCouponPicker();

  const tags = useMemo(() => {
    const result = [...couponTag];

    if (visibility === COUPONS.VISIBILITY_STATUS.APP_ONLY) {
      result.unshift(COUPONS.BADGES.APP_ONLY);
    }

    return result;
  }, [couponTag]);

  const onOpenCouponPicker = () => {
    sendEvent({
      action: formatGaEventActionByVisibility('coupon_click', visibility),
      category: gaEventCategory,
    });

    if (!isCouponBlocked(visibility)) {
      openCouponPicker({ couponId, storeName, storeSlug });
      return;
    }

    onUnloggedUserTriesOpenExclusiveCoupon(visibility, () => {
      openCouponPicker({ couponId, storeName, storeSlug });
    });
  };

  const onRetrieveCoupon = () => {
    retrieveCoupon({ couponId, storeName, storeSlug });
  };

  return (
    <Box
      className={twMerge(
        'grid min-h-36 gap-x-6 gap-y-2 rounded-4 border-2 border-neutral-high-200 p-4 dark:border-neutral-low-200 md:grid-cols-5 md:gap-x-8 md:px-8 md:py-6',
        className
      )}
    >
      <div className="row-span-2 flex h-24 w-28 flex-col items-center justify-center self-center border-r border-dashed border-neutral-high-400 pr-6 dark:border-neutral-low-200 md:pr-8 xl:w-40">
        {withCouponStoreCover ? (
          <Image
            alt={`Imagem da loja ${storeName}`}
            className="rounded-3"
            height={96}
            src={getStoreImageUrl(storeImage, 160)}
            width={96}
          />
        ) : (
          <>
            <Heading
              as="h3"
              className="break-all text-center text-neutral-low-400 dark:text-neutral-high-200"
              size="size2"
            >
              {getSimplifiedCouponDiscount(couponDiscountShort)}
            </Heading>
            <Text className="break-all text-center" size="size1">
              {placeholder('subtitles.coupon')}
            </Text>
          </>
        )}
      </div>
      <Heading
        as="h2"
        className="col-start-2 break-all text-neutral-low-400 dark:text-neutral-high-200 md:col-span-2"
        size="size4"
      >
        {couponTitle}
      </Heading>
      {tags.length > 0 && (
        <div className="col-start-2 row-start-2 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <Tag
              key={tag}
              className="max-w-32 whitespace-normal md:max-w-fit md:whitespace-pre"
              filled={tag === COUPONS.BADGES.APP_ONLY}
              size="size3"
              type={tag === COUPONS.BADGES.APP_ONLY ? 'secondary' : 'neutral'}
            >
              {tag}
            </Tag>
          ))}
        </div>
      )}
      {isCouponVisible && visibility !== COUPONS.VISIBILITY_STATUS.APP_ONLY ? (
        <Button
          className="col-span-2 row-span-2 row-start-3 select-none self-center justify-self-end border-2 border-dashed border-neutral-high-400 dark:border-neutral-high-400 md:col-start-4 md:row-span-2 md:w-64"
          size="size3"
          type="neutral-stroke"
          full
          onClick={onRetrieveCoupon}
        >
          {couponCode || COUPONS.COUPON_WITH_NO_CODE}
        </Button>
      ) : (
        <Button
          className="group relative col-span-2 row-span-2 row-start-3 self-center justify-self-end md:col-start-4 md:row-span-2 md:w-64"
          size="size3"
          type="neutral"
          full
          onClick={onOpenCouponPicker}
        >
          <div className="absolute -right-px -top-px size-6 rounded-bl-[0.45rem] rounded-tr-[0.45rem] border-2 border-dashed border-neutral-high-400 bg-neutral-high-100 transition-all duration-200 ease-linear after:absolute after:-right-[2px] after:-top-[2px] after:size-0 after:rounded-bl-[7px] after:border-b-0 after:border-l-[24px] after:border-r-0 after:border-t-[24px] after:border-solid after:border-b-transparent after:border-l-primary-300 after:border-r-transparent after:border-t-transparent after:transition-all after:duration-200 after:ease-linear after:content-empty group-hover:size-8 group-hover:after:border-l-[32px] group-hover:after:border-t-[32px] dark:bg-neutral-low-300" />
          {placeholder('actions.pickCoupon')}
        </Button>
      )}
    </Box>
  );
};

CouponListItem.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  couponCode: PropTypes.string.isRequired,
  couponDiscountShort: PropTypes.string.isRequired,
  couponId: PropTypes.number.isRequired,
  couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  couponTitle: PropTypes.string.isRequired,
  gaEventCategory: PropTypes.string.isRequired,
  isCouponVisible: PropTypes.bool,
  storeImage: PropTypes.string.isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
  withCouponStoreCover: PropTypes.bool,
};

export default CouponListItem;
